import React, { useEffect } from 'react'

// components
import HomepageHead from './components/HomepageHead/HomepageHead'
import HeroSection from './components/HeroSection/HeroSection'
import SalonsBookingForm from './components/SalonsBookingForm/SalonsBookingForm'
import RecommendedSalonsSection from './components/RecommendedSalonsSection/RecommendedSalonsSection'
import AppPromoSection from './components/AppPromoSection/AppPromoSection'
import DiscoverSalonSection from './components/DiscoverSalonSection/DiscoverSalonSection'
import DiscoverServiceSection from './components/DiscoverServiceSection/DiscoverServiceSection'
import SelectedCategorySalonsSection from './components/SelectedCategorySalonsSection/SelectedCategorySalonsSection'
import AppFeatureSection from './components/AppFeatureSection/AppFeatureSection'
import ReviewsSection from './components/TestimonialsSection/TestimonialsSection'
import StatisticsSection from './components/NpStatisticsSection/NpStatisticsSection'
import NpForBusinessSection from './components/NpForBusinessSection/NpForBusinessSection'

// types
import {
	GetSalonsDataParams,
	CategoriesResponse,
	ConfigResponse,
	SalonsResponse,
	HomePageQuery,
	CmsHomepageData,
	PageProps,
	HomePageContainerQuery
} from '../../types/types'

// utils
import createPageContainer from '../../utils/createPageContainer'
import { RECOMMENDED_SALONS_PARAMS, SELECTED_CATEGORY_ID, SELECTED_CATEGORY_SALONS_PARAMS } from './utils'
import { getHomePageVideoStartEvent, getPageViewEvent, setHomePageViewEventTimingStart } from '../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../utils/dataLayer'
import { shopsConfigById } from '../../appDefaults'

// styles
import * as SC from './HomePageStyles'

// types
type RouteProps = {}

type HomePageData = {
	configData: ConfigResponse
	categoriesData: CategoriesResponse
	recommendedSalonsData: SalonsResponse | undefined
	selectedCategorySalonsData: SalonsResponse | undefined
	selectedCategoryData: CategoriesResponse['categories'][number] | undefined
	homepageCmsData: CmsHomepageData
}

type HomePageProps = PageProps<HomePageData, HomePageQuery, RouteProps>

// page
const HomePage = (props: HomePageProps) => {
	const { configData, categoriesData, recommendedSalonsData, selectedCategorySalonsData, selectedCategoryData, homepageCmsData } = props
	// GA page view event
	useEffect(() => {
		const pageViewEvent = getPageViewEvent('salons_homepage', 'salons_homepage')
		pushToDataLayer(pageViewEvent)
		setHomePageViewEventTimingStart()
	}, [])

	return (
		<>
			<HomepageHead metaData={homepageCmsData.metaData} />
			<HeroSection cmsData={homepageCmsData.heroData} categoriesData={categoriesData} />
			<SalonsBookingForm categoriesData={categoriesData} configData={configData} />
			<SC.HomePageWrapper>
				<RecommendedSalonsSection recommendedSalonsData={recommendedSalonsData?.salons} />
				<AppPromoSection cmsData={homepageCmsData.appPromoData} onPlayClick={(name) => pushToDataLayer(getHomePageVideoStartEvent(name))} />
				<DiscoverSalonSection cmsData={homepageCmsData.discoverSalonData} />
				<DiscoverServiceSection cmsData={homepageCmsData.discoverServiceData} categoriesData={categoriesData} />
				{/**
				 * Display recommended salons for the selected category only if the category exists.
				 * (The ID of the selected category is hardcoded on the frontend, so although it is not expected, the category might be deleted. Therefore, this case needs to be handled.)
				 * If the selected category is not found, display the Recommended Salons section again as a fallback.
				 */}
				{selectedCategoryData ? (
					<SelectedCategorySalonsSection
						selectedCategorySalonsData={selectedCategorySalonsData?.salons}
						selectedCategoryData={selectedCategoryData}
					/>
				) : (
					<RecommendedSalonsSection recommendedSalonsData={recommendedSalonsData?.salons} />
				)}
				<AppFeatureSection cmsData={homepageCmsData.appFeatureData} />
				<ReviewsSection cmsData={homepageCmsData.npTestimonialsData} />
				<StatisticsSection cmsData={homepageCmsData.npStatisticsData} />
				<NpForBusinessSection cmsData={homepageCmsData.npForBusinessData} />
			</SC.HomePageWrapper>
		</>
	)
}

const HomePageContainer = createPageContainer<HomePageData, HomePageContainerQuery, RouteProps>({
	identifier: 'HomePage',
	PageComponent: HomePage,
	initDefaultData: async ({ api, query, shopId }) => {
		const { countryCode } = shopsConfigById[shopId]

		const [categoriesData, configData, homepageCmsData] = await Promise.all([
			api.b2c.getCategoriesData(),
			api.b2c.getConfigData(),
			api.cms.getHomepageCmsData(countryCode)
		])

		const selectedCategoryData = categoriesData.categories.find((category) => SELECTED_CATEGORY_ID === category.id)
		let recommendedSalonsData: SalonsResponse | undefined
		let selectedCategorySalonsData: SalonsResponse | undefined

		try {
			const getSalonsDataParams: GetSalonsDataParams = {
				...RECOMMENDED_SALONS_PARAMS,
				lonMy: query.lonMy,
				latMy: query.latMy
			}

			const recommendedSalonsPromise = api.b2c.getSalonsData(getSalonsDataParams)

			let selectedCategorySalonsPromise: Promise<SalonsResponse> | undefined

			if (selectedCategoryData) {
				const getSelectedCategorySalonsDataParams: GetSalonsDataParams = {
					...SELECTED_CATEGORY_SALONS_PARAMS,
					lonMy: query.lonMy,
					latMy: query.latMy
				}

				selectedCategorySalonsPromise = api.b2c.getSalonsData(getSelectedCategorySalonsDataParams)
			}

			;[recommendedSalonsData, selectedCategorySalonsData] = await Promise.all([recommendedSalonsPromise, selectedCategorySalonsPromise])
		} catch {
			/* empty */
		}

		return {
			recommendedSalonsData,
			selectedCategorySalonsData,
			selectedCategoryData,
			categoriesData,
			configData,
			homepageCmsData
		}
	}
})

export default HomePageContainer
